import { Button } from "@/components/ui/button";
import { CustomerSearchResult, type CustomerSearchResultModel } from "./CustomerSearchResult";
import { useState } from "react";

export function CustomerCreateOverride({
  candidates,
  onSelectExisting,
  onConfirmCreateNew,
  disabled,
  reviewRequired = true,
}: {
  candidates: CustomerSearchResultModel[];
  onSelectExisting: (id: number, name: string) => void;
  onConfirmCreateNew: (reason: string) => void;
  disabled?: boolean;
  /** When false, matches are advisory and create-new does not require an override. */
  reviewRequired?: boolean;
}) {
  const [ack, setAck] = useState(false);
  const [reason, setReason] = useState("");
  if (candidates.length === 0) return null;
  return (
    <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50 p-3">
      <p className="text-sm font-semibold text-amber-950">Possible existing customers</p>
      <p className="text-xs text-amber-900">
        {reviewRequired
          ? "Review these matches and select the correct record. Creating another customer requires an explicit override."
          : "These records share similar details. You can open the correct one or continue creating a new customer."}
      </p>
      <ul className="space-y-2">
        {candidates.map((c) => (
          <li key={c.id} className="flex items-start justify-between gap-2 rounded border border-amber-100 bg-white px-2 py-2">
            <CustomerSearchResult customer={c} />
            <Button type="button" size="sm" variant="outline" onClick={() => onSelectExisting(c.id, c.name)}>
              Use this record
            </Button>
          </li>
        ))}
      </ul>
      {reviewRequired ? (
        <>
          <label className="flex items-start gap-2 text-xs text-amber-950">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" />
            I reviewed every match and none of these is the customer.
          </label>
          {ack ? (
            <div className="space-y-2">
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this a different customer?"
                className="w-full rounded border border-amber-200 px-2 py-1.5 text-sm"
                rows={2}
              />
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={disabled || reason.trim().length < 8}
                onClick={() => onConfirmCreateNew(reason.trim())}
              >
                None of these is the customer — create a new record
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
